import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import axios from 'axios';
// We don't need useNavigate anymore since we are staying on the same page
import { Calendar as CalendarIcon, Briefcase, ChevronRight, AlertCircle } from 'lucide-react';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './CandidateCalendar.css';

const localizer = momentLocalizer(moment);

// 1. Accept the new prop here
const CandidateCalendar = ({ onSwitchToJobs }) => {
  const [events, setEvents] = useState([]);
  const [date, setDate] = useState(new Date());
  const [view, setView] = useState('month');

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/job-postings`);
        const jobs = response.data;
        const calendarEvents = [];

        jobs.forEach((job) => {
          if (job.applyByDate) {
            calendarEvents.push({
              id: job._id,
              title: `Deadline: ${job.role}`,
              company: job.companyName,
              start: new Date(job.applyByDate),
              end: new Date(job.applyByDate),
              allDay: true,
              type: 'deadline',
            });
          }
          if (job.dateOfVisit) {
            calendarEvents.push({
              id: job._id + '_visit',
              title: `Visit: ${job.companyName}`,
              company: job.companyName,
              start: new Date(job.dateOfVisit),
              end: new Date(job.dateOfVisit),
              allDay: true,
              type: 'visit',
            });
          }
        });
        setEvents(calendarEvents);
      } catch (error) {
        console.error('Error fetching jobs:', error);
      }
    };
    fetchJobs();
  }, []);

  const handleNavigate = (newDate) => setDate(newDate);
  const handleViewChange = (newView) => setView(newView);

  // 2. Update the button handler to call the prop
  const handleViewAllJobs = () => {
    if (onSwitchToJobs) {
      onSwitchToJobs(); // This switches the Parent Dashboard tab to "Jobs"
    }
  };

  const eventStyleGetter = (event) => {
    let backgroundColor = '#3b82f6';
    let borderLeft = '4px solid #1d4ed8';

    if (event.type === 'deadline') {
      backgroundColor = '#fee2e2';
      borderLeft = '4px solid #ef4444';
    } else if (event.type === 'visit') {
      backgroundColor = '#dbeafe';
      borderLeft = '4px solid #2563eb';
    }

    return {
      style: {
        backgroundColor,
        color: '#1f2937',
        border: 'none',
        borderLeft,
        borderRadius: '4px',
        fontSize: '0.85rem',
        fontWeight: '500',
        padding: '2px 5px',
        display: 'block',
      },
    };
  };

  const upcomingEvents = events
    .filter(e => new Date(e.start) >= new Date())
    .sort((a, b) => new Date(a.start) - new Date(b.start))
    .slice(0, 5);

  return (
    <div className="calendar-wrapper">
      <div className="calendar-page-header">
        <div className="header-title">
          <div className="icon-box"><CalendarIcon size={24} color="#4f46e5" /></div>
          <div>
            <h2>Schedule & Deadlines</h2>
            <p>Track your application deadlines and campus visit dates.</p>
          </div>
        </div>
      </div>

      <div className="calendar-layout">
        <div className="calendar-main">
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 600 }}
            eventPropGetter={eventStyleGetter}
            views={['month', 'agenda']}
            
            // Calendar Controls
            date={date}
            view={view}
            onNavigate={handleNavigate}
            onView={handleViewChange}
            
            selectable
            popup
          />
        </div>

        <div className="calendar-sidebar">
          <div className="agenda-card">
            <div className="agenda-header">
              <h3><AlertCircle size={18} /> Upcoming Priority</h3>
            </div>
            
            <div className="agenda-list">
              {upcomingEvents.length > 0 ? (
                upcomingEvents.map((evt, idx) => (
                  <div key={idx} className={`agenda-item ${evt.type}`}>
                    <div className="agenda-date">
                      <span className="month">{moment(evt.start).format('MMM')}</span>
                      <span className="day">{moment(evt.start).format('DD')}</span>
                    </div>
                    <div className="agenda-details">
                      <h4>{evt.title}</h4>
                      <div className="meta">
                        <span className="company"><Briefcase size={12}/> {evt.company}</span>
                        {evt.type === 'visit' && <span className="tag visit">Campus Visit</span>}
                        {evt.type === 'deadline' && <span className="tag deadline">Deadline</span>}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-events"><p>No upcoming deadlines found.</p></div>
              )}
            </div>
            
            <div className="agenda-footer">
              <button className="view-all-btn" onClick={handleViewAllJobs}>
                View All Jobs <ChevronRight size={16}/>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateCalendar;